/**
 * FindPpl — People discovery page with real Firestore data.
 * Routes: /find-ppl, /meet-ppl, /connect-with
 *
 * Auth model:
 *  - Auth-gated — Cloud Functions require auth for matching against the caller's profile
 *  - Anonymous visitors see a teaser view with blurred cards + "Log in to connect" CTA
 */

import React, { useEffect, useState, useCallback } from 'react';
import { MapPin, Heart, Zap, Users, AlertCircle, Search, Smartphone, Star } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthGate } from '../hooks/useAuthGate';
import GlassPanel from '../components/GlassPanel';
import MonoAvatar from '../components/MonoAvatar';
import ProfileNudgeCard, { type ProfileNudgeProfile } from '../components/ProfileNudgeCard';
import { getMyProfile } from '../services/accountService';
import {
  getPplByLocation,
  getPplByInterest,
  getPplOnline,
  getInteractedPpl,
  type FindPplTab,
} from '../services/findPplService';
import type { DiscoveredUser } from '../types/pplTalk';
import '../styles/modern.css';

// ─── Tab config ─────────────────────────────────────────────────────────────

interface TabConfig {
  id: FindPplTab;
  label: string;
  icon: React.ComponentType<any>;
}

const TABS: TabConfig[] = [
  { id: 'location', label: 'Near you', icon: MapPin },
  { id: 'interests', label: 'Interests', icon: Heart },
  { id: 'online', label: 'Online now', icon: Zap },
  { id: 'interacted', label: 'I know', icon: Users },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

function hashHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}

function getUserName(u: DiscoveredUser): string {
  return u.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.handle || 'User';
}

function getUserHandle(u: DiscoveredUser): string {
  return u.handle ? `@${u.handle.replace(/^@/, '')}` : '';
}

function getLocationText(u: DiscoveredUser): string {
  return [u.city, u.country].filter(Boolean).join(', ');
}

function getTagLabel(u: DiscoveredUser, tab: FindPplTab): string {
  switch (tab) {
    case 'location':
      if (u.distance) return u.distance;
      if (u.city) return 'Same city';
      if (u.country) return 'Same country';
      return '';
    case 'interests':
      if (u.score) return `${u.score}% match`;
      return 'Shared interest';
    case 'online':
      return u.status || 'Online now';
    case 'interacted': {
      const sources = (u.matchType || '').split(',').filter(Boolean);
      if (sources.includes('chat')) return 'Chatted';
      if (sources.includes('reply')) return 'Replied';
      if (sources.includes('like')) return 'Liked';
      return 'Interacted';
    }
    default:
      return '';
  }
}

// ─── Anonymous teaser ───────────────────────────────────────────────────────

function AnonTeaser({ onLogin }: { onLogin: () => void }) {
  const placeholders = Array.from({ length: 6 });

  return (
    <div className="findppl-anon-teaser">
      <div className="findppl-redesign-grid blurred">
        {placeholders.map((_, i) => (
          <GlassPanel key={i} padding={0}>
            <div style={{ height: 56, background: 'rgba(255,255,255,0.04)', borderRadius: '16px 16px 0 0' }} />
            <div style={{ padding: '0 16px 16px', marginTop: -22 }}>
              <MonoAvatar name={`placeholder${i}`} size={44} />
              <div className="skel-line wide pulse" style={{ height: 14, marginTop: 10 }} />
              <div className="skel-line medium pulse" style={{ height: 10, marginTop: 6 }} />
              <div className="skel-line narrow pulse" style={{ height: 10, marginTop: 6 }} />
            </div>
          </GlassPanel>
        ))}
      </div>
      <div className="findppl-anon-overlay">
        <div className="findppl-anon-cta">
          <Users size={32} />
          <h2>discover people near you</h2>
          <p>
            log in to find and connect with real people based on your location,
            interests, and who's online right now.
          </p>
          <button className="primary-button" onClick={onLogin}>
            log in to connect
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Person card (new design) ───────────────────────────────────────────────

function PersonCard({
  user: u,
  tab,
  onClick,
}: {
  user: DiscoveredUser;
  tab: FindPplTab;
  onClick: () => void;
}) {
  const name = getUserName(u);
  const handle = getUserHandle(u);
  const location = getLocationText(u);
  const tag = getTagLabel(u, tab);
  const hue = hashHue(name);
  const isOnline = tab === 'online' || u.status === 'Online now';

  return (
    <GlassPanel padding={0} onClick={onClick}>
      {/* mini gradient hero */}
      <div
        style={{
          position: 'relative',
          height: 56,
          borderRadius: '16px 16px 0 0',
          background: `linear-gradient(135deg, oklch(0.45 0.22 ${hue}), oklch(0.55 0.25 ${(hue + 50) % 360}))`,
          overflow: 'hidden',
        }}
      >
        {isOnline && (
          <span className="findppl-online-pill">
            <span className="findppl-online-pill-dot" />
            ONLINE
          </span>
        )}
      </div>

      <div style={{ padding: '0 16px 16px', marginTop: -22 }}>
        {u.avatar ? (
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <img
              src={u.avatar}
              alt={name}
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #1a0e22',
              }}
            />
          </div>
        ) : (
          <MonoAvatar name={name} size={44} />
        )}

        <div className="findppl-card-name">{name}</div>
        {handle && <div className="findppl-card-handle">{handle}</div>}
        {location && <div className="findppl-card-location">{location}</div>}
        {tag && (
          <div style={{ marginTop: 8 }}>
            <span className="tag-pill tag-pill--sm tag-pill--violet">{tag}</span>
          </div>
        )}
      </div>
    </GlassPanel>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export default function FindPpl() {
  const { user } = useAuth();
  const { isAuthenticated, redirectToLogin } = useAuthGate();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<FindPplTab>('location');
  const [users, setUsers] = useState<DiscoveredUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [lastDocId, setLastDocId] = useState<string | undefined>();
  const [totalCount, setTotalCount] = useState<number | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);
  const [tabCounts, setTabCounts] = useState<Record<FindPplTab, number | undefined>>({
    location: undefined,
    interests: undefined,
    online: undefined,
    interacted: undefined,
  });
  // The caller's OWN profile, read only so the "get matched" card knows whether
  // it is needed. Re-read on window focus so the card is gone when the user
  // comes back from the editor in another tab or from the browser's back stack.
  const [ownProfile, setOwnProfile] = useState<ProfileNudgeProfile | null>(null);

  // ─── Fetch users ──────────────────────────────────────────────────────
  const fetchUsers = useCallback(
    async (tab: FindPplTab, append = false) => {
      if (!user?.uid) return;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }

      try {
        const data = tab === 'interacted'
          ? await getInteractedPpl(20)
          : await (tab === 'location'
              ? getPplByLocation
              : tab === 'interests'
                ? getPplByInterest
                : getPplOnline)({
              userId: user.uid,
              limit: 20,
              lastDocId: append ? lastDocId : undefined,
              getTotalCount: !append,
            });

        const filtered = (data.users || []).filter((u) => u.profileSet !== false);

        if (append) {
          setUsers((prev) => [...prev, ...filtered]);
        } else {
          setUsers(filtered);
        }

        setHasMore(data.hasMore ?? false);
        setLastDocId(data.lastDocId);
        if (data.totalCount !== undefined) {
          setTotalCount(data.totalCount);
          setTabCounts((prev) => ({ ...prev, [tab]: data.totalCount }));
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to load people');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [user?.uid, lastDocId]
  );

  // Load on tab change
  useEffect(() => {
    if (isAuthenticated) {
      setUsers([]);
      setLastDocId(undefined);
      setHasMore(false);
      setTotalCount(undefined);
      fetchUsers(activeTab);
    }
  }, [activeTab, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Own profile (the "get matched" card) ─────────────────────────────
  const refreshOwnProfile = useCallback(async () => {
    if (!user?.uid) {
      setOwnProfile(null);
      return;
    }
    try {
      setOwnProfile((await getMyProfile(user.uid)) as ProfileNudgeProfile);
    } catch {
      // A failed read must not invent a gap — leave the card as it was.
    }
  }, [user?.uid]);

  useEffect(() => {
    if (!isAuthenticated) {
      setOwnProfile(null);
      return undefined;
    }
    // Mount is enough: react-router unmounts this page on the way to the
    // editor and remounts it on the way back. A window 'focus' listener would
    // spend a users/{uid} read on every alt-tab for no extra coverage.
    refreshOwnProfile();
    return undefined;
  }, [isAuthenticated, refreshOwnProfile]);

  const handleLoadMore = useCallback(() => {
    fetchUsers(activeTab, true);
  }, [activeTab, fetchUsers]);

  const navigateToProfile = (u: DiscoveredUser) => {
    const id = u.id || u.uid;
    if (id) navigate(`/user/${id}`);
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <>

      <div className="page-shell">
        <div className="page-shell__inner" style={{ maxWidth: 920 }}>
          {/* Page header */}
          <div className="page-header">
            <div>
              <h1 className="page-header__title">find people</h1>
              {totalCount !== undefined && (
                <span className="page-header__count">{totalCount} people</span>
              )}
              <div className="page-header__subtitle">
                people who match your circles, vibe, or are around right now.
              </div>
            </div>
            {isAuthenticated && (
              <button
                className="findppl-f8-btn"
                onClick={() => navigate('/f8chat')}
              >
                <Star size={13} />
                <span>Ask f8 to suggest</span>
              </button>
            )}
          </div>

          {/* Thin profile — matching has nothing to go on (PPL-486) */}
          {isAuthenticated && (
            <ProfileNudgeCard profile={ownProfile} style={{ marginBottom: 18 }} />
          )}

          {/* Pill tabs */}
          {isAuthenticated && (
            <div style={{ marginBottom: 18 }}>
              <div className="pill-tabs">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    className={`pill-tab ${activeTab === tab.id ? 'pill-tab--active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {React.createElement(tab.icon, { size: 13 })}
                    {tab.label}
                    {tabCounts[tab.id] !== undefined && (
                      <span className="pill-tab__count">{tabCounts[tab.id]}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Anonymous teaser */}
          {!isAuthenticated && <AnonTeaser onLogin={redirectToLogin} />}

          {/* Loading */}
          {isAuthenticated && loading && (
            <div className="findppl-redesign-grid">
              {Array.from({ length: 8 }).map((_, i) => (
                <GlassPanel key={i} padding={0}>
                  <div style={{ height: 56, background: 'rgba(255,255,255,0.04)', borderRadius: '16px 16px 0 0' }} />
                  <div style={{ padding: '0 16px 16px', marginTop: -22 }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} className="pulse" />
                    <div className="skel-line wide pulse" style={{ height: 14, marginTop: 10 }} />
                    <div className="skel-line medium pulse" style={{ height: 10, marginTop: 6 }} />
                  </div>
                </GlassPanel>
              ))}
            </div>
          )}

          {/* Error */}
          {isAuthenticated && error && !loading && (
            <div className="findppl-error">
              <AlertCircle size={20} />
              <p>{error}</p>
              <button className="primary-button" onClick={() => fetchUsers(activeTab)}>
                try again
              </button>
            </div>
          )}

          {/* Results grid */}
          {isAuthenticated && !loading && !error && (
            <>
              {users.length === 0 ? (
                <div className="findppl-empty">
                  <Search size={20} />
                  <h3>no people found</h3>
                  <p>
                    {activeTab === 'location' && 'no users found near your location yet.'}
                    {activeTab === 'interests' && 'no users matching your interests yet.'}
                    {activeTab === 'online' && 'no users online right now. check back later!'}
                  </p>
                </div>
              ) : (
                <div className="findppl-redesign-grid">
                  {users.map((u) => (
                    <PersonCard
                      key={u.id || u.uid}
                      user={u}
                      tab={activeTab}
                      onClick={() => navigateToProfile(u)}
                    />
                  ))}
                </div>
              )}

              {hasMore && (
                <div style={{ marginTop: 28, textAlign: 'center' }}>
                  <button
                    className="circles-load-more-btn"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? 'loading...' : 'load more'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
